import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, PencilSimple, Trash, X, ArrowUp, ArrowDown } from "@phosphor-icons/react";

/* Field schema per content type */
const SCHEMAS = {
  products: {
    title: "Products",
    fields: [
      { k: "title", l: "Title", t: "text", req: true },
      { k: "slug", l: "Slug (URL)", t: "text", req: true },
      { k: "tagline", l: "Tagline", t: "text" },
      { k: "industry", l: "Primary industry", t: "text" },
      { k: "industries", l: "Extra industry tags (comma-separated)", t: "list" },
      { k: "price_aed_monthly", l: "Monthly price (AED)", t: "number" },
      { k: "preview_key", l: "Preview mockup key (bank/hotel/hospital/school/mlm/home/lms/manufacturing/restaurant/property/hr/logistics)", t: "text" },
      { k: "features", l: "Features (one per line)", t: "textlist" },
      { k: "body_md", l: "Long description (Markdown)", t: "textarea" },
      { k: "tour_steps", l: "Interactive tour steps", t: "steplist" },
      { k: "demo_available", l: "Live demo available", t: "bool" },
      { k: "image_url", l: "Cover image URL (optional)", t: "text" },
      { k: "order", l: "Order", t: "number" },
    ],
    empty: { title: "", slug: "", tagline: "", industry: "", industries: [], price_aed_monthly: 0, preview_key: "bank", features: [], body_md: "", tour_steps: [], demo_available: true, image_url: "", order: 0 },
  },
  "web-packages": {
    title: "Web packages",
    fields: [
      { k: "name", l: "Name", t: "text", req: true },
      { k: "tagline", l: "Tagline", t: "text" },
      { k: "price_inr", l: "Price (INR)", t: "number", req: true },
      { k: "features", l: "Features (one per line)", t: "textlist" },
      { k: "highlight", l: "Highlight as most popular", t: "bool" },
      { k: "order", l: "Order", t: "number" },
    ],
    empty: { name: "", tagline: "", price_inr: 0, features: [], highlight: false, order: 0 },
  },
  "hosting-plans": {
    title: "Hosting plans",
    fields: [
      { k: "name", l: "Name", t: "text", req: true },
      { k: "tagline", l: "Tagline", t: "text" },
      { k: "price_inr_monthly", l: "Price / month (INR)", t: "number", req: true },
      { k: "region", l: "Region", t: "text", req: true },
      { k: "features", l: "Features (one per line)", t: "textlist" },
      { k: "highlight", l: "Highlight as most popular", t: "bool" },
      { k: "order", l: "Order", t: "number" },
    ],
    empty: { name: "", tagline: "", price_inr_monthly: 0, region: "UAE", features: [], highlight: false, order: 0 },
  },
  "marketing-tiers": {
    title: "Marketing tiers",
    fields: [
      { k: "name", l: "Name", t: "text", req: true },
      { k: "tagline", l: "Tagline", t: "text" },
      { k: "price_inr_monthly", l: "Price / month (INR)", t: "number", req: true },
      { k: "features", l: "Features (one per line)", t: "textlist" },
      { k: "highlight", l: "Highlight", t: "bool" },
      { k: "order", l: "Order", t: "number" },
    ],
    empty: { name: "", tagline: "", price_inr_monthly: 0, features: [], highlight: false, order: 0 },
  },
  "case-studies": {
    title: "Case studies",
    fields: [
      { k: "title", l: "Title", t: "text", req: true },
      { k: "slug", l: "Slug (URL)", t: "text", req: true },
      { k: "client", l: "Client", t: "text", req: true },
      { k: "industry", l: "Industry", t: "text" },
      { k: "summary", l: "Summary", t: "textarea" },
      { k: "body_md", l: "Long story (Markdown)", t: "textarea" },
      { k: "result", l: "Outcome / result", t: "text" },
      { k: "client_quote", l: "Client quote", t: "textarea" },
      { k: "client_role", l: "Client role", t: "text" },
      { k: "cta_label", l: "CTA label", t: "text" },
      { k: "image_url", l: "Image URL", t: "text" },
      { k: "order", l: "Order", t: "number" },
    ],
    empty: { title: "", slug: "", client: "", industry: "", summary: "", body_md: "", result: "", client_quote: "", client_role: "", cta_label: "Talk to us about a similar build", metrics: [], image_url: "", order: 0 },
  },
  careers: {
    title: "Careers",
    fields: [
      { k: "title", l: "Role title", t: "text", req: true },
      { k: "location", l: "Location", t: "text", req: true },
      { k: "type", l: "Type", t: "text" },
      { k: "description", l: "Description", t: "textarea", req: true },
      { k: "responsibilities", l: "Responsibilities (one per line)", t: "textlist" },
      { k: "order", l: "Order", t: "number" },
      { k: "active", l: "Active / visible", t: "bool" },
    ],
    empty: { title: "", location: "", type: "Full-time", description: "", responsibilities: [], order: 0, active: true },
  },
  "global-reach": {
    title: "Global reach",
    fields: [
      { k: "city", l: "City", t: "text", req: true },
      { k: "country", l: "Country", t: "text", req: true },
      { k: "flag", l: "Flag emoji", t: "text" },
      { k: "note", l: "Note", t: "text" },
      { k: "order", l: "Order", t: "number" },
    ],
    empty: { city: "", country: "", flag: "", note: "", order: 0 },
  },
};

export default function ContentAdmin({ type }) {
  const schema = SCHEMAS[type];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get(`/${type}`);
    setItems(r.data);
  };
  useEffect(() => { load(); }, [type]);

  const save = async (val) => {
    try {
      if (val.id) {
        const { id, ...rest } = val;
        await api.put(`/${type}/${id}`, rest);
      } else {
        await api.post(`/${type}`, val);
      }
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/${type}/${id}`); load();
  };

  return (
    <div data-testid={`admin-${type}`} className="p-8 md:p-10">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display font-black text-3xl md:text-4xl">{schema.title}</h1>
        <button onClick={() => setEditing({ ...schema.empty })} data-testid={`${type}-new`} className="btn-brand text-sm py-2.5 px-4">
          <Plus size={16} weight="bold" /> New
        </button>
      </div>

      <div className="mt-6 bg-white border border-[color:var(--line)]">
        {items.map((it) => (
          <div key={it.id} data-testid={`${type}-row-${it.id}`} className="p-5 border-b border-[color:var(--line)] flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{it.title || it.name || it.city}</div>
              <div className="text-xs text-neutral-500 truncate">{it.tagline || it.summary || it.description || it.country || it.region}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditing({ ...it })} data-testid={`${type}-edit-${it.id}`} className="border border-[color:var(--line)] p-2 hover:border-black transition-colors">
                <PencilSimple size={14} />
              </button>
              <button onClick={() => del(it.id)} data-testid={`${type}-del-${it.id}`} className="border border-[color:var(--line)] p-2 hover:border-[color:var(--brand)] hover:text-[color:var(--brand)] transition-colors">
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-neutral-500 text-sm">Nothing here yet.</div>}
      </div>

      {editing && (
        <EditModal
          value={editing}
          schema={schema}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function EditModal({ value, schema, onClose, onSave }) {
  const [v, setV] = useState(value);
  const set = (k, val) => setV({ ...v, [k]: val });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-4 overflow-auto">
      <div className="bg-white w-full max-w-2xl border border-[color:var(--ink)] my-8">
        <div className="p-5 flex items-center justify-between border-b border-[color:var(--line)]">
          <h2 className="font-display font-bold text-xl">{v.id ? "Edit" : "New"} {schema.title.replace(/s$/, "")}</h2>
          <button onClick={onClose} data-testid="edit-close" className="p-2"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {schema.fields.map((f) => (
            <div key={f.k}>
              <label className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{f.l}{f.req && " *"}</label>
              {f.t === "text" && (
                <input value={v[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`field-${f.k}`}
                  className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]" />
              )}
              {f.t === "textarea" && (
                <textarea rows={4} value={v[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} data-testid={`field-${f.k}`}
                  className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]" />
              )}
              {f.t === "number" && (
                <input type="number" value={v[f.k] ?? 0} onChange={(e) => set(f.k, Number(e.target.value))} data-testid={`field-${f.k}`}
                  className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]" />
              )}
              {f.t === "bool" && (
                <label className="mt-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!v[f.k]} onChange={(e) => set(f.k, e.target.checked)} data-testid={`field-${f.k}`} className="accent-[color:var(--brand)]" />
                  Enabled
                </label>
              )}
              {f.t === "list" && (
                <input value={(v[f.k] || []).join(", ")} onChange={(e) => set(f.k, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid={`field-${f.k}`}
                  className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)]" />
              )}
              {f.t === "textlist" && (
                <textarea rows={4} value={(v[f.k] || []).join("\n")} onChange={(e) => set(f.k, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} data-testid={`field-${f.k}`}
                  className="mt-1 w-full border border-[color:var(--line)] px-4 py-2.5 outline-none focus:border-[color:var(--brand)] font-mono text-sm" />
              )}
              {f.t === "steplist" && (
                <StepListEditor value={v[f.k] || []} onChange={(next) => set(f.k, next)} testidPrefix={f.k} />
              )}
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-[color:var(--line)] flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-sm py-2.5 px-4">Cancel</button>
          <button onClick={() => onSave(v)} data-testid="edit-save" className="btn-brand text-sm py-2.5 px-4">Save</button>
        </div>
      </div>
    </div>
  );
}

function StepListEditor({ value, onChange, testidPrefix }) {
  const steps = value || [];
  const sync = (next) => onChange(next);
  const add = () => sync([...steps, { title: "", body: "" }]);
  const remove = (i) => sync(steps.filter((_, idx) => idx !== i));
  const update = (i, k, val) => sync(steps.map((s, idx) => idx === i ? { ...s, [k]: val } : s));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const copy = [...steps]; [copy[i], copy[j]] = [copy[j], copy[i]]; sync(copy);
  };
  return (
    <div className="mt-2 border border-[color:var(--line)] bg-neutral-50 p-3 space-y-3" data-testid={`${testidPrefix}-editor`}>
      {steps.length === 0 && (
        <div className="text-xs text-neutral-500">No custom steps — the tour will use the default 4-step template. Add steps below to override.</div>
      )}
      {steps.map((s, i) => (
        <div key={i} className="bg-white border border-[color:var(--line)] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--brand)]">Step {i + 1}</div>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="border border-[color:var(--line)] p-1 disabled:opacity-30 hover:border-black" aria-label="Move up"><ArrowUp size={12} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="border border-[color:var(--line)] p-1 disabled:opacity-30 hover:border-black" aria-label="Move down"><ArrowDown size={12} /></button>
              <button type="button" onClick={() => remove(i)} data-testid={`${testidPrefix}-del-${i}`} className="border border-[color:var(--line)] p-1 hover:border-[color:var(--brand)] hover:text-[color:var(--brand)]" aria-label="Delete step"><Trash size={12} /></button>
            </div>
          </div>
          <input data-testid={`${testidPrefix}-title-${i}`} value={s.title || ""} onChange={(e) => update(i, "title", e.target.value)} placeholder="Step title" className="w-full border border-[color:var(--line)] px-3 py-2 outline-none focus:border-[color:var(--brand)] text-sm font-semibold" />
          <textarea data-testid={`${testidPrefix}-body-${i}`} rows={2} value={s.body || ""} onChange={(e) => update(i, "body", e.target.value)} placeholder="What happens in this step…" className="mt-2 w-full border border-[color:var(--line)] px-3 py-2 outline-none focus:border-[color:var(--brand)] text-sm" />
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`${testidPrefix}-add`} className="btn-ghost text-xs py-2 px-3">
        <Plus size={12} weight="bold" /> Add step
      </button>
    </div>
  );
}
